import React, { useState, useRef } from 'react';

// Lib
import { motion } from 'framer-motion';

// Stylings
import './select.scss';

// Hooks
import { useClickOutside } from '@arctheme-hooks/useClickOutside';

type Props = {
  id?: string;
  placeholder?: string;
  options: { name: string; value: string }[];
  hasError?: boolean;
  errorMessage?: string;
  label?: string;
  onChange: (val: string | { target: { name: string; value: string } }) => void;
  disabled?: boolean;
  className?: string;
  selected?: string;
  returnEvent?: boolean;
};

const Select = ({
  id,
  placeholder,
  options,
  hasError,
  errorMessage,
  label,
  onChange,
  disabled,
  className = '',
  selected,
  returnEvent = false
}: Props) => {
  const selectedOption = options?.find((option) => option.value === selected)
    ? options.find((option) => option.value === selected)
    : null;

  // States
  const [showOptions, setShowOptions] = useState(false);

  // Refs
  const wrapperRef = useRef(null);
  const handleClickOutside = () => {
    setShowOptions(false);
  };

  // Hooks
  useClickOutside(wrapperRef, handleClickOutside);

  return (
    <div
      ref={wrapperRef}
      className={`field flex flex-col items-start ${className} ${hasError ? 'has-error' : ''}`}
    >
      <div className='flex flex-col-reverse w-full'>
        <div
          className={`custom-select ${disabled ? 'disabled' : ''} ${showOptions ? 'active' : ''}`}
          onClick={() => setShowOptions((prev) => !prev)}
        >
          <div className={`custom-select__value ${!selectedOption ? 'custom-select__value--placeholder' : ''}`}>
            {selectedOption ? selectedOption.name : placeholder}
          </div>

          {options.length > 0 && (
            <motion.div
              className='custom-select__options'
              initial={false}
              animate={{ opacity: showOptions ? 1 : 0 }}
            >
              <ul>
                {options.map((option) => (
                  <li
                    key={option.value}
                    className={`custom-select__option py-2 ${selectedOption && option.value === selectedOption.value ? 'custom-select__option--active' : ''}`}
                    onClick={() => {
                      if (returnEvent && id) {
                        onChange({
                          target: {
                            name: id,
                            value: option.value
                          }
                        });
                      } else {
                        onChange(option.value);
                      }
                    }}
                  >
                    {option.name}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>

        {label && <div className='label'>{label}</div>}
      </div>

      {errorMessage && hasError && <p className='errors mt-1'>{errorMessage}</p>}
    </div>
  );
};

export default Select;
